import { useMemo } from 'react';
import {
  Alert,
  Box,
  Card,
  CardContent,
  CardHeader,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import Grid from '@mui/material/Grid';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../contexts/PermissionContext';
import { useNumberFormat } from '../hooks/useNumberFormat';
import { useCurrentSubscription } from '../hooks/useCurrentSubscription';
import { useSubscriptionUsagePeriods } from '../hooks/useSubscriptionUsagePeriods';
import { formatDateOnly } from '../utils/date';
import type {
  AuthBootstrapSubscriptionState,
  AuthBootstrapSubscriptionSummary,
  SubscriptionPlanType,
} from '../types';
import SubscriptionMinutePacksTable from './SubscriptionMinutePacksTable';
import SubscriptionUsagePeriodsTable from './SubscriptionUsagePeriodsTable';

const statusChipColor: Record<string, 'default' | 'success' | 'warning' | 'error' | 'info'> = {
  active: 'success',
  past_due: 'warning',
  canceled: 'error',
  inactive: 'warning',
};

interface SubscriptionOverviewProps {
  showHeader?: boolean;
  subscriptionType?: SubscriptionPlanType;
}

/**
 * Resolve the lightweight subscription summary already available from auth bootstrap.
 *
 * The detailed subscription endpoint still owns billing-heavy fields like minute
 * packs and price history, but this snapshot prevents blank states when the app
 * only needs current-plan metadata.
 */
function resolveBootstrapSubscriptionSummary(
  subscriptions: {
    ai: AuthBootstrapSubscriptionState;
    reverseIvr: AuthBootstrapSubscriptionState;
  } | null,
  subscriptionType?: SubscriptionPlanType,
): AuthBootstrapSubscriptionSummary | null {
  if (!subscriptions) {
    return null;
  }

  if (subscriptionType === 'ai') {
    return subscriptions.ai.summary;
  }
  if (subscriptionType === 'reverse_ivr') {
    return subscriptions.reverseIvr.summary;
  }

  const activeSummaries = [subscriptions.ai.summary, subscriptions.reverseIvr.summary].filter(Boolean);
  if (activeSummaries.length === 1) {
    return activeSummaries[0] ?? null;
  }
  return null;
}

export default function SubscriptionOverview({
  showHeader = true,
  subscriptionType,
}: SubscriptionOverviewProps) {
  const { t } = useTranslation(['pages', 'common']);
  const { subscriptions } = useAuth();
  const { hasPermission } = usePermissions();
  const { locale, formatNumber, formatDuration, formatCurrency } = useNumberFormat();
  const { subscription, loading, error, isReady } = useCurrentSubscription({ subscriptionType });
  const bootstrapSummary = useMemo(
    () => resolveBootstrapSubscriptionSummary(subscriptions, subscriptionType),
    [subscriptionType, subscriptions],
  );
  const effectiveSubscription = subscription ?? bootstrapSummary;
  const hasDetailedSubscription = Boolean(subscription);
  const canReadDetailedSubscription = hasPermission('subscriptions_read');
  const planType: SubscriptionPlanType = effectiveSubscription?.plan?.planType ?? 'ai';
  const isReverseIvrPlan = planType === 'reverse_ivr';
  const {
    usagePeriods,
    isLoading: usagePeriodsLoading,
    error: usagePeriodsError,
  } = useSubscriptionUsagePeriods({
    subscriptionId: subscription?.id,
    ordering: '-period_start',
    permissionCode: 'subscriptions_read',
    enabled: Boolean(subscription?.id) && canReadDetailedSubscription,
  });

  const planLimitLabel = useMemo(() => {
    if (!effectiveSubscription) return '-';
    return effectiveSubscription.plan.maxMinutesSeconds === -1
      ? t('common:subscription.unlimited')
      : formatDuration(effectiveSubscription.plan.maxMinutesSeconds);
  }, [effectiveSubscription, formatDuration, t]);

  const concurrencyLabel = useMemo(() => {
    if (!effectiveSubscription) return '-';
    return effectiveSubscription.maxConcurrentCalls === -1
      ? t('common:subscription.unlimited')
      : formatNumber(effectiveSubscription.maxConcurrentCalls);
  }, [effectiveSubscription, formatNumber, t]);

  if (loading && !effectiveSubscription && !error) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  if (error && !effectiveSubscription) {
    return (
      <Alert severity="error">
        {t('pages:subscription.errors.fetch', { message: error.message || '' })}
      </Alert>
    );
  }

  if (!effectiveSubscription && isReady) {
    return (
      <Alert severity="info">
        {t('pages:subscription.errors.noSubscription')}
      </Alert>
    );
  }

  if (!effectiveSubscription) {
    return null;
  }

  return (
    <Box>
      {showHeader && (
        <>
          <Typography variant="h4" gutterBottom>
            {t('pages:subscription.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary" gutterBottom>
            {t('pages:subscription.subtitle')}
          </Typography>
        </>
      )}
      <Grid container spacing={2}>
        <Grid size={12}>
          <Card>
            <CardHeader
              title={t('pages:subscription.planCard.title')}
              subheader={effectiveSubscription.plan.name}
              action={
                <Chip
                  label={t(`common:subscription.status.${effectiveSubscription.status}`)}
                  color={statusChipColor[effectiveSubscription.status] || 'default'}
                  size="small"
                />
              }
            />
            <Divider />
            <CardContent>
              <Stack spacing={1}>
                {hasDetailedSubscription ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2">{t('pages:subscription.planCard.price')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {formatCurrency(
                        Number(subscription!.plan.priceAmount),
                        subscription!.plan.priceCurrency,
                        2
                      )} / {t(`pages:plans.intervals.${subscription!.plan.billingInterval}`)}
                    </Typography>
                  </Stack>
                ) : null}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2">{t('pages:subscription.planCard.period')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {formatDateOnly(effectiveSubscription.currentPeriodStart, { locale })}
                    {' \u2192 '}
                    {effectiveSubscription.currentPeriodEnd
                      ? formatDateOnly(effectiveSubscription.currentPeriodEnd, { locale })
                      : t('common:subscription.noEndDate')}
                  </Typography>
                </Stack>
                {!isReverseIvrPlan ? (
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <Typography variant="subtitle2">{t('pages:subscription.planCard.planLimit')}</Typography>
                    <Typography variant="body2" color="text.secondary">
                      {planLimitLabel}
                    </Typography>
                  </Stack>
                ) : null}
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Typography variant="subtitle2">{t('pages:subscription.planCard.concurrency')}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {concurrencyLabel}
                  </Typography>
                </Stack>
              </Stack>
            </CardContent>
          </Card>
        </Grid>
        {hasDetailedSubscription && !isReverseIvrPlan ? (
          <Grid size={12}>
            <SubscriptionMinutePacksTable
              title={t('pages:subscription.packsCard.title')}
              subtitle={t('pages:subscription.packsCard.subtitle')}
              packs={subscription!.minutePacks}
              emptyMessage={t('pages:subscription.packsCard.empty')}
            />
          </Grid>
        ) : null}
        {hasDetailedSubscription ? (
          <Grid size={12}>
            <SubscriptionUsagePeriodsTable
              title={t('pages:subscription.history.title')}
              subtitle={t('pages:subscription.history.subtitle')}
              usagePeriods={usagePeriods}
              loading={usagePeriodsLoading}
              error={usagePeriodsError?.message || null}
              emptyMessage={t('pages:subscription.history.empty')}
              statusPresentation="public"
            />
          </Grid>
        ) : null}
      </Grid>
    </Box>
  );
}
